import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import createFuzzySearch from '@nozbe/microfuzz';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { wagtailToConfig } from '../../utils/wagtailToConfig';
import { configHasInheritCountryFilter } from '../../utils/filterParams';
import s from './ImportFromWagtail.module.scss';

const STEPS = { DASHBOARD: 'dashboard', PAGE: 'page', BLOCK: 'block' };

const CHART_TYPE_LABELS = {
    fenix_line_chart: 'Line',
    fenix_stacked_area_chart: 'Area',
    fenix_column_chart: 'Column',
    fenix_bar_chart: 'Bar',
    fenix_polar_chart: 'Polar',
    fenix_tree_map_chart: 'Treemap',
    fenix_timeline_chart: 'Timeline',
    fenix_mixed_chart: 'Mixed',
};

const ImportFromWagtail = ({ onImport, hasExistingConfig }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState(STEPS.DASHBOARD);
    const [selectedDashboard, setSelectedDashboard] = useState(null);
    const [selectedPage, setSelectedPage] = useState(null);

    const [dashboards, setDashboards] = useState([]);
    const [pages, setPages] = useState([]);
    const [blocks, setBlocks] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [confirmBlock, setConfirmBlock] = useState(null);

    const reset = useCallback(() => {
        setStep(STEPS.DASHBOARD);
        setSelectedDashboard(null);
        setSelectedPage(null);
        setDashboards([]);
        setPages([]);
        setBlocks([]);
        setError(null);
        setSearchQuery('');
        setConfirmBlock(null);
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
        reset();
    }, [reset]);

    const fetchData = useCallback(async (url) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchWithAuth(url);
            if (!response) return null;
            if (!response.ok)
                throw new Error(`Request failed: ${response.status}`);
            return await response.json();
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDashboards = useCallback(async () => {
        const data = await fetchData('/wt/fenix/graphs/import/dashboards/');
        if (data) setDashboards(data);
    }, [fetchData]);

    const loadPages = useCallback(
        async (dashboardId) => {
            const data = await fetchData(
                `/wt/fenix/graphs/import/pages/${dashboardId}/`
            );
            if (data) setPages(data);
        },
        [fetchData]
    );

    const loadBlocks = useCallback(
        async (pageId) => {
            const data = await fetchData(
                `/wt/fenix/graphs/import/blocks/${pageId}/`
            );
            if (data) setBlocks(data);
        },
        [fetchData]
    );

    const loadDashboardsRef = useRef(loadDashboards);
    loadDashboardsRef.current = loadDashboards;

    useEffect(() => {
        if (isOpen) {
            loadDashboardsRef.current();
        }
    }, [isOpen]);

    const handleDashboardClick = useCallback(
        (dashboard) => {
            setSelectedDashboard(dashboard);
            setStep(STEPS.PAGE);
            setSearchQuery('');
            loadPages(dashboard.id);
        },
        [loadPages]
    );

    const handlePageClick = useCallback(
        (page) => {
            setSelectedPage(page);
            setStep(STEPS.BLOCK);
            loadBlocks(page.id);
        },
        [loadBlocks]
    );

    const doImport = useCallback(
        (block) => {
            const result = wagtailToConfig(block.block_json);
            if (result.success) {
                // Default the preview's "Inherited country" to the country
                // the source page would resolve __inherit_country_code__ to.
                if (
                    block.page_country_code &&
                    configHasInheritCountryFilter(result.config)
                ) {
                    result.config.previewInheritCountry =
                        block.page_country_code;
                }
                const sourceInfo = block.location
                    ? {
                          ...block.location,
                          dashboardTitle: selectedDashboard?.title,
                          pageTitle: selectedPage?.title,
                          blockTitle: block.title,
                      }
                    : null;
                onImport(result.config, sourceInfo);
                close();
            } else {
                setError(result.errors?.join(', ') || 'Import failed');
                setConfirmBlock(null);
            }
        },
        [onImport, close, selectedDashboard, selectedPage]
    );

    const handleBlockClick = useCallback(
        (block) => {
            if (hasExistingConfig) {
                setConfirmBlock(block);
            } else {
                doImport(block);
            }
        },
        [hasExistingConfig, doImport]
    );

    const handleBreadcrumbDashboard = useCallback(() => {
        setStep(STEPS.DASHBOARD);
        setSelectedDashboard(null);
        setSelectedPage(null);
        setPages([]);
        setBlocks([]);
        setSearchQuery('');
        setConfirmBlock(null);
    }, []);

    const handleBreadcrumbPage = useCallback(() => {
        setStep(STEPS.PAGE);
        setSelectedPage(null);
        setBlocks([]);
        setSearchQuery('');
        setConfirmBlock(null);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (confirmBlock) {
                    setConfirmBlock(null);
                } else {
                    close();
                }
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, close, confirmBlock]);

    const fuzzySearch = useMemo(() => {
        if (pages.length === 0) return null;
        return createFuzzySearch(pages, {
            getText: (item) => [item.title, item.path],
        });
    }, [pages]);

    const filteredPages = useMemo(() => {
        if (!searchQuery.trim() || !fuzzySearch) return pages;
        return fuzzySearch(searchQuery).map((result) => result.item);
    }, [searchQuery, fuzzySearch, pages]);

    const renderBreadcrumb = () => {
        if (step === STEPS.DASHBOARD) return null;

        return (
            <div className={s.Breadcrumb}>
                {step === STEPS.PAGE && (
                    <>
                        <button
                            className={s.BreadcrumbLink}
                            onClick={handleBreadcrumbDashboard}>
                            {selectedDashboard?.title}
                        </button>
                        <span className={s.BreadcrumbSeparator}>&rsaquo;</span>
                        <span className={s.BreadcrumbCurrent}>Pages</span>
                    </>
                )}
                {step === STEPS.BLOCK && (
                    <>
                        <button
                            className={s.BreadcrumbLink}
                            onClick={handleBreadcrumbDashboard}>
                            {selectedDashboard?.title}
                        </button>
                        <span className={s.BreadcrumbSeparator}>&rsaquo;</span>
                        <button
                            className={s.BreadcrumbLink}
                            onClick={handleBreadcrumbPage}>
                            {selectedPage?.title}
                        </button>
                        <span className={s.BreadcrumbSeparator}>&rsaquo;</span>
                        <span className={s.BreadcrumbCurrent}>Blocks</span>
                    </>
                )}
            </div>
        );
    };

    const renderContent = () => {
        if (loading) {
            return <div className={s.StateMessage}>Loading...</div>;
        }

        if (error) {
            const retryFn =
                step === STEPS.DASHBOARD
                    ? loadDashboards
                    : step === STEPS.PAGE
                      ? () => loadPages(selectedDashboard.id)
                      : () => loadBlocks(selectedPage.id);

            return (
                <div className={s.ErrorState}>
                    <span>{error}</span>
                    <button className={s.RetryButton} onClick={retryFn}>
                        Retry
                    </button>
                </div>
            );
        }

        if (step === STEPS.DASHBOARD) {
            if (dashboards.length === 0) {
                return (
                    <div className={s.StateMessage}>No dashboards found</div>
                );
            }
            return (
                <ul className={s.List}>
                    {dashboards.map((d) => (
                        <li
                            key={d.id}
                            className={s.ListItem}
                            onClick={() => handleDashboardClick(d)}>
                            <span className={s.ListItemTitle}>{d.title}</span>
                            <span className={s.Arrow}>&rsaquo;</span>
                        </li>
                    ))}
                </ul>
            );
        }

        if (step === STEPS.PAGE) {
            if (pages.length === 0) {
                return (
                    <div className={s.StateMessage}>
                        No pages with Fenix blocks found
                    </div>
                );
            }
            if (filteredPages.length === 0) {
                return <div className={s.StateMessage}>No matching pages</div>;
            }
            return (
                <ul className={s.List}>
                    {filteredPages.map((p) => (
                        <li
                            key={p.id}
                            className={s.ListItem}
                            onClick={() => handlePageClick(p)}>
                            <div className={s.ListItemContent}>
                                <span className={s.ListItemTitle}>
                                    {p.title}
                                </span>
                                <span className={s.ListItemPath}>{p.path}</span>
                            </div>
                            <span className={s.ListItemMeta}>
                                {p.block_count}{' '}
                                {p.block_count === 1 ? 'block' : 'blocks'}
                            </span>
                            <span className={s.Arrow}>&rsaquo;</span>
                        </li>
                    ))}
                </ul>
            );
        }

        if (step === STEPS.BLOCK) {
            if (blocks.length === 0) {
                return (
                    <div className={s.StateMessage}>No Fenix blocks found</div>
                );
            }
            return (
                <ul className={s.List}>
                    {blocks.map((b) => (
                        <li
                            key={b.id}
                            className={s.ListItem}
                            onClick={() => handleBlockClick(b)}>
                            <span className={s.ListItemTitle}>{b.title}</span>
                            <span className={s.ChartTypeBadge}>
                                {CHART_TYPE_LABELS[b.chart_type] ||
                                    b.chart_type}
                            </span>
                        </li>
                    ))}
                </ul>
            );
        }

        return null;
    };

    const modal =
        isOpen &&
        createPortal(
            <div className={s.Overlay} onClick={close}>
                <div className={s.Modal} onClick={(e) => e.stopPropagation()}>
                    <div className={s.Header}>
                        <h3 className={s.Title}>Import from Wagtail</h3>
                        <button className={s.CloseButton} onClick={close}>
                            &times;
                        </button>
                    </div>
                    {renderBreadcrumb()}
                    {step === STEPS.PAGE && pages.length > 0 && !loading && (
                        <div className={s.SearchWrapper}>
                            <input
                                className={s.SearchInput}
                                type="text"
                                placeholder="Search pages..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                            />
                        </div>
                    )}
                    <div className={s.Body}>{renderContent()}</div>

                    {confirmBlock && (
                        <div
                            className={s.ConfirmOverlay}
                            onClick={() => setConfirmBlock(null)}>
                            <div onClick={(e) => e.stopPropagation()}>
                                <p className={s.ConfirmMessage}>
                                    Replace current config with &ldquo;
                                    {confirmBlock.title}&rdquo;?
                                </p>
                                <div className={s.ConfirmActions}>
                                    <button
                                        className={s.ConfirmCancel}
                                        onClick={() => setConfirmBlock(null)}>
                                        Cancel
                                    </button>
                                    <button
                                        className={s.ConfirmImport}
                                        onClick={() => doImport(confirmBlock)}>
                                        Import
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>,
            document.body
        );

    return (
        <>
            <button className={s.ImportButton} onClick={() => setIsOpen(true)}>
                Import from Wagtail
            </button>
            {modal}
        </>
    );
};

export default ImportFromWagtail;
